import type {
  ContratApprentissageDTO,
  ContratDTO,
  EmployeurDTO,
  FormationDTO,
  RemunerationDTO,
  RepresentantLegalDTO,
  StagiaireContratApprentissageDTO,
  TutoratDTO,
} from '@/features/api/extranet/dto/Demande/CAPP'
import type { FichierDTO } from '@/features/api/extranet/dto/Demande/shared'
import {
  addPreBuilder,
  type Flag,
  type ModelValidateurResult,
  type Regles,
  targetPreBuilder,
} from '@/features/domaine/moteur-regles'
import { PropertyStatus } from '@/features/domaine/property-status'
import { LevelRegleEnum } from '@/features/domaine/types/opco/shared'
import {
  checkDateInRange,
  dateFormatter,
  getAge,
  getNbJour,
  getToday,
  isDate,
  isNotNullOrUndefined,
  isNullOrUndefined,
  isNullUndefinedOrEmptyString,
  isNumber,
} from '@/features/shared/tools'
import {
  preAdherentOuMandatGestion,
  preContratMoins6Moins,
  preContratPlus36Moins,
  preDateValide,
  preEquals,
  preMajeur,
  preMineur,
  preMoins16Ans,
  preNafTT,
  preNirTemporaireNon,
  preNotEquals,
  preNotNirTemporaire,
  preNotNullOrEmpty,
} from './preconditions'
import type { ContextReglesCapp, ReglesCAPP } from './types'

import type { DateRange, EtablissementAdherentDTO } from '@/features/api/extranet/dto/shared'
import { CODE_POSTAL_MAYOTTE } from '../../constantes'
import {
  checkNir,
  checkNirDateNaissance,
  checkNirDepartement,
  checkNirSexe,
  getRangeDate,
  getSmicHoraireBrut,
  idccAutoriseTT,
  isAdherentOuMandatGestion,
  preNombreValide,
  rgEstCodePostalFactory,
  rgEstCommuneFactory,
  rgEstDate,
  rgEstMailFactory,
  rgEstMaxLengthFactory,
  rgEstNomOuPrenomFactory,
  rgEstNumeric,
  rgEstNumericMaxFactory,
  rgEstNumericMinFactory,
  rgEstNumTelFactory,
  rgEstObligatoireFactory,
  rgEstVoieFactory,
  rgEstVraiFactory,
  rgTestRegExpFactory,
} from '../shared'
import { SAISIE_INVALIDE, SAISIE_OBLIGATOIRE } from '../shared/messages-erreurs'
import { typeContratAvenant, typeContratAvenantRncp, typeContratSuccessionContrat } from './constantes'
import {
  getRangeDateConclusion,
  getRangeDateDebutExecutionContrat,
  getRangeDateDebutFormation,
  getRangeDateDebutFormationChezEmployeur,
  getRangeDateEffetAvenant,
  getRangeDateFinContratOuPeriodeApprentissage,
  getRangeDatePrevueFinEpreuvesOuExamens,
  getRemunerationProperty,
} from './tools'
import { TypesContextesAutorisesEnum } from '@/features/auth/context-app'

/* Initalisation des helpers */
const targetPre = targetPreBuilder<ContratApprentissageDTO, ContextReglesCapp>()
const addPre = addPreBuilder<ContratApprentissageDTO, ContextReglesCapp>()

/* Declaration de type pour les sous modeles */
type RegleCAPPMembre<T extends object> = Regles<T, ContextReglesCapp, LevelRegleEnum, Flag, ContratApprentissageDTO>
type ReglesCAPPApprenti = RegleCAPPMembre<StagiaireContratApprentissageDTO>
// type ReglesCAPPContact = RegleCAPPMembre<ContactDTO>
type ReglesCAPPContrat = RegleCAPPMembre<ContratDTO>
type ReglesCAPPEmployeur = RegleCAPPMembre<EmployeurDTO>
type ReglesCAPPEmployeurEtablissement = RegleCAPPMembre<EtablissementAdherentDTO>
type ReglesCAPPFichier = RegleCAPPMembre<FichierDTO>
type ReglesCAPPFormation = RegleCAPPMembre<FormationDTO>
type ReglesCAPPRemuneration = RegleCAPPMembre<RemunerationDTO>
type ReglesCAPPRepresentantLegal = RegleCAPPMembre<RepresentantLegalDTO>
type ReglesCAPPTutorat = RegleCAPPMembre<TutoratDTO>

/* EMPLOYEUR --------------------------------------------------------------------- */
/* Etablissement */
const reglesEtablissementEmployeur: ReglesCAPPEmployeurEtablissement = {
  /* en lecture seule */
  adresse: {},
  denomination: {},
  siret: {},
  typeEntreprise: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  },
  idcc: {
    code: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.etablissement?.idcc?.code?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Technique),
      ),
      /*
      'test-idcc': {
        validateur: (value, _parent, _modele, context) => {
          return context.lov.IdccKeys.has(value ?? '')
        },
        message: SAISIE_INVALIDE,
        level: LevelRegleEnum.Technique,
      },*/
      controleIdccPermanentInterimaire: {
        pre: [preNotNullOrEmpty, preNafTT],
        validateur: (value, _parent, _modele) => !!value && idccAutoriseTT.includes(value),
        message: "Veuillez sélectionner soit l'IDCC 1413 lié aux permanents soit l'IDCC 2378 lié aux intérimaires",
        level: LevelRegleEnum.Metier,
      },
    },
  },
  naf: {
    code: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.employeur?.etablissement?.naf?.code?.$status === PropertyStatus.Required,
        rgEstObligatoireFactory(LevelRegleEnum.Technique),
      ),
      /*
      'test-naf': {
        validateur: (value, _parent, _modele, context) => {
          return context.lov.NafKeys.has(value ?? '')
        },
        message: SAISIE_INVALIDE,
        level: LevelRegleEnum.Technique,
      },
      */
    },
  },
}

/* Employeur */
const reglesEmployeur: ReglesCAPPEmployeur = {
  etablissement: reglesEtablissementEmployeur,
  typeEmployeur: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.employeur?.employeurSpecifique?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    /*
    controlePublic: {
      pre: [preNotNullOrEmpty, targetPre('employeur.secteurEntreprise', preEquals('Public'))],
      validateur: (value) => {
        return value === 'EtablissementPublicIndustrielCommercial'
      },
      message: 'AKTO ne prend pas en charge les demandes pour ce type d’employeur.',
      level: LevelRegleEnum.Metier,
    },
    */
  },
  employeurSpecifique: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.employeur?.typeEmployeur?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
  effectifTotalEntreprise: {
    ...rgEstNumeric,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.employeur?.typeEmployeur?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
    ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique),
    ...rgEstNumericMaxFactory(1000000, LevelRegleEnum.Technique),
  },
  regimeSpecifiqueAssuranceChomage: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.employeur?.regimeSpecifiqueAssuranceChomage?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
  contact: {
    fonction: {
      ...rgEstMaxLengthFactory(250),
    },

    email: {
      ...rgEstMaxLengthFactory(200),
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      ...rgEstMailFactory(LevelRegleEnum.Metier),
    },
    telephone: {
      ...rgEstMaxLengthFactory(10),
      ...rgEstNumTelFactory(LevelRegleEnum.Metier),
    },
  },
  secteurEntreprise: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.employeur?.secteurEntreprise?.$status === PropertyStatus.Required,
      rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ),
  },
}

/* CONTRAT ----------------------------------------------------------------------- */
const reglesContrat: ReglesCAPPContrat = {
  typeContratAvenant: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.typeContratAvenant?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  modeContractuel: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.modeContractuel?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  numeroDecaPrecedent: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.numeroDecaPrecedent?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...rgEstMaxLengthFactory(15),
  },
  typeDerogation: {
    /* Fix- PRT-784 */ /*
    controleMoins16Ans: {
      pre: [
        //preNotNullOrEmpty,
        //preEquals('AgeInferieur16'),
        targetPre('apprenti.dateNaissance', preDateValide),
        targetPre('contrat.dateConclusion', preDateValide),
      ],
      validateur: (value, _parent, modele) => {
        const age = getAge(
          new Date(modele.apprenti!.dateNaissance!),
          new Date(modele.contrat!.dateConclusion!),
        )
        console.log("Validateur", age, value)
        if (age < 16) {
          return ['AgeInferieur16', 'CumulDerogations'].includes(value ?? '')
        } else {
          return  value !== 'AgeInferieur16'
        }
      },
      message: "L'apprenti doit être âgé de moins de 16 ans pour conclure ce contrat",
      level: LevelRegleEnum.Metier,
    },
    */
    /* Fix- PRT-784 */
    controleDerogationAgeInferieur16: {
      pre: [
        preNotNullOrEmpty,
        preEquals('AgeInferieur16'),
        targetPre('apprenti.dateNaissance', preDateValide),
        targetPre('contrat.dateConclusion', preDateValide),
      ],
      validateur: (value, _parent, modele) => {
        const age = getAge(new Date(modele.apprenti!.dateNaissance!), new Date(modele.contrat!.dateConclusion!))

        if (age >= 16) {
          return value !== 'AgeInferieur16'
        }
      },
      message: "L'apprenti doit être âgé de moins de 16 ans pour conclure ce contrat",
      level: LevelRegleEnum.Metier,
    },
    // Remarque : J'aurai plutot mis celle-ci sur la date de daissance
    controle16Ans30Ans: {
      pre: [
        preNotNullOrEmpty,
        preNotEquals('AgeInferieur16', 'AgeSuperieur29', 'CumulDerogations', 'AutreDerogation'),
        targetPre('apprenti.dateNaissance', preDateValide),
        targetPre('contrat.dateConclusion', preDateValide),
      ],
      validateur: (_value, _parent, modele) => {
        const age = getAge(new Date(modele.apprenti!.dateNaissance!), new Date(modele.contrat!.dateConclusion!))

        return age >= 16 && age <= 30
      },
      message: "L'apprenti doit être âgé de 16 à 30 ans pour conclure ce contrat",
      level: LevelRegleEnum.Metier,
    },
    // Remarque : Vérifier la régle dans le fichier Excel

    controlePlus29Ans: {
      pre: [
        preNotNullOrEmpty,
        preEquals('AgeSuperieur29'),
        targetPre('apprenti.dateNaissance', preDateValide),
        targetPre('contrat.dateConclusion', preDateValide),
      ],
      validateur: (_value, _parent, modele) =>
        getAge(new Date(modele.apprenti!.dateNaissance!), new Date(modele.contrat!.dateConclusion!)) > 29,
      message: "L'apprenti doit être âgé de plus de 30 ans pour bénéficier de cette dérogation",
      level: LevelRegleEnum.Metier,
    },
  },
  dateConclusion: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) => context.status.contrat?.dateConclusion?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleRange: {
      pre: [preNotNullOrEmpty],
      validateur: (value) => checkDateInRange(new Date(value!), getRangeDateConclusion()),
      message: 'Le dossier ne peut être soumis qu’à partir de la date de conclusion (signature du contrat).',
      level: LevelRegleEnum.Metier,
    },
  },
  dateDebutExecutionContrat: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.dateDebutExecutionContrat?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleRange: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateConclusion', preDateValide)],
      validateur: (value, _parent, modele) =>
        checkDateInRange(
          new Date(value!),
          getRangeDateDebutExecutionContrat(new Date(modele.contrat!.dateConclusion!)),
        ),
      message:
        "La date de début d'exécution du contrat doit être postérieure ou égale à la date de conclusion (signature du contrat)",
      level: LevelRegleEnum.Metier,
    },
    /*
    controle15Ans: {
      pre: [preNotNullOrEmpty, targetPre('apprenti.dateNaissance', preDateValide)],
      validateur: (value, _parent, modele) => {
        const dateDebut = new Date(value!)
        const dateNaissanceAnnee = new Date(modele.apprenti?.dateNaissance!)
        dateNaissanceAnnee.setFullYear(dateDebut.getFullYear())

        const age = getAge(new Date(modele.apprenti?.dateNaissance!), dateDebut)
        const nbJour = getNbJour(dateNaissanceAnnee, dateDebut)

        return age > 15 || (age === 15 && nbJour >= 1)
      },
      message: 'Un apprenti doit avoir 15 ans et 1 jour à la date de début du contrat.',
      level: LevelRegleEnum.Metier,
    },*/
  },
  dateDebutFormationChezEmployeur: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.dateDebutFormationChezEmployeur?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleRange: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateDebutExecutionContrat', preDateValide)],
      validateur: (value, _parent, modele) =>
        checkDateInRange(
          new Date(value!),
          getRangeDateDebutFormationChezEmployeur(
            new Date(modele.contrat!.dateDebutExecutionContrat!),
            isDate(modele.contrat?.dateFinContratOuPeriodeApprentissage)
              ? new Date(modele.contrat.dateFinContratOuPeriodeApprentissage)
              : null,
          ),
        ),
      message:
        'La date de formation pratique doit être postérieure ou égale à la date d’exécution du contrat et avant la date de fin',
      level: LevelRegleEnum.Metier,
    },
  },
  dateEffetAvenant: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.dateEffetAvenant?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleRange: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateConclusion', preDateValide)],
      validateur: (value, _parent, modele) =>
        checkDateInRange(new Date(value!), getRangeDateEffetAvenant(new Date(modele.contrat!.dateConclusion!))),
      message: "La date d'effet de l'avenant doit être supérieure à la date de conclusion du contrat",
      level: LevelRegleEnum.Metier,
    },
  },
  dateFinContratOuPeriodeApprentissage: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.dateFinContratOuPeriodeApprentissage?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleDateDebutContrat: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateDebutExecutionContrat', preDateValide)],
      validateur: (value, _parent, modele) =>
        checkDateInRange(new Date(value!), getRangeDate(new Date(modele.contrat!.dateDebutExecutionContrat!), null)),
      message: 'La date de fin de contrat doit être supérieure à la date de début',
      level: LevelRegleEnum.Metier,
    },
    controleDureeMax: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateDebutExecutionContrat', preDateValide)],
      validateur: (value, _parent, modele) =>
        checkDateInRange(
          new Date(value!),
          getRangeDateFinContratOuPeriodeApprentissage(new Date(modele.contrat!.dateDebutExecutionContrat!)),
        ),
      message: 'Un contrat ne peut jamais dépasser 48 mois',
      level: LevelRegleEnum.Metier,
    },

    controle6Mois: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateDebutExecutionContrat', preDateValide), preContratMoins6Moins],
      validateur: (_value, _parent, modele) =>
        typeContratAvenant.includes(modele.contrat?.typeContratAvenant ?? '') ||
        modele.contrat?.typeContratAvenant === 'NouveauContratPrecedentRompu',
      message: 'La durée du minimum du contrat est de 6 mois pour le type de contrat sélectionné.',
      level: LevelRegleEnum.Metier,
    },
    controle36Mois: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateDebutExecutionContrat', preDateValide), preContratPlus36Moins],
      validateur: (_value, _parent, modele) => modele.apprenti?.beneficieReconnaissanceTravailleurHandicape === true,
      message: 'La durée maximum du contrat est de 36 mois si l’apprenti n’est pas reconnu travailleur handicapé',
      level: LevelRegleEnum.Metier,
    },
    controleAvantDateFinFormation: {
      pre: [preNotNullOrEmpty, targetPre('contrat.dateFinContratOuPeriodeApprentissage', preDateValide)],
      validateur: (value, _parent, modele) =>
        new Date(value!) >= new Date(modele.formation!.datePrevueFinEpreuvesOuExamens!),
      message: 'La date prévue de fin des épreuves doit intervenir avant la fin du contrat.',
      level: LevelRegleEnum.Metier,
    },
  },

  dureeHebdomadaire: {
    heure: {
      ...rgEstNumeric,
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.contrat?.dureeHebdomadaire?.heure?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      format: {
        pre: [preNotNullOrEmpty],
        validateur: (_value, parent) => {
          return !isNullOrUndefined(parent.minute)
        },
        message: SAISIE_INVALIDE,
        level: LevelRegleEnum.Technique,
      },
      controleMinimumHandicapSportif: {
        pre: [preNotNullOrEmpty],
        validateur: (value, parent, model) => {
          if (
            model.apprenti?.beneficieReconnaissanceTravailleurHandicape === true ||
            model.apprenti?.inscritSurListeSportifs === true
          ) {
            const duree: number = Number(value) + (parent.minute ?? 0.0) / 100
            return duree >= 10
          }

          return true
        },
        message:
          'La durée minimum de travail hebdomadaire est de 10h00 pour les apprentis en situation de handicap ou sportif de haut niveau.La durée minimum de travail hebdomadaire est de 35h00',
        level: LevelRegleEnum.Metier,
      },
      controleMinimum: {
        pre: [preNotNullOrEmpty, preMajeur],
        validateur: (value, parent, model) => {
          if (
            model.apprenti?.beneficieReconnaissanceTravailleurHandicape === true ||
            model.apprenti?.inscritSurListeSportifs === true
          )
            return true
          const duree: number = Number(value) + (parent.minute ?? 0.0) / 100
          return duree >= 35
        },
        message: 'La durée minimum de travail hebdomadaire est de 35h00',
        level: LevelRegleEnum.Metier,
      },
      controleMineur: {
        pre: [preNotNullOrEmpty, preMineur],
        validateur: (value, parent) => {
          const duree: number = Number(value) + (parent.minute ?? 0.0) / 100
          return duree <= 35
        },
        message: 'Avant 18 ans, la durée hebdomadaire de travail ne peut excéder 35 h',
        level: LevelRegleEnum.Metier,
      },

      controlePlus18Ans: {
        pre: [preNotNullOrEmpty, preMajeur],
        validateur: (value, parent) => {
          const duree: number = Number(value) + (parent.minute ?? 0.0) / 100
          return duree <= 48
        },
        message: 'Après 18 ans, la durée hebdomadaire de travail ne peut excéder 48 h',
        level: LevelRegleEnum.Metier,
      },
    },
  },
  travailMachinesDangereusesOuExpositionRisquesparticuliers: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.travailMachinesDangereusesOuExpositionRisquesparticuliers?.$status ===
        PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  caisseRetraitreComplementaire: {
    ...rgEstMaxLengthFactory(100),
  },
  mobiliteInternationale: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  },
  lieu: {
    ...addPre((_value, _parent, _model, context) => context.status.contrat?.lieu?.$status === PropertyStatus.Required, {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    }),
  },
  conditionRealisation: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.conditionRealisation?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  appliquerMajoration10Pourcent: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.contrat?.appliquerMajoration10Pourcent?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
}

/* APPRENTI ---------------------------------------------------------------------- */
const reglesApprenti: ReglesCAPPApprenti = {
  nomNaissance: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  prenom: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  nom: {
    ...rgEstMaxLengthFactory(50),
    ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
  },
  nirTemporaire: {
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
  },
  nir: {
    ...addPre(preNotNirTemporaire, rgEstMaxLengthFactory(13, LevelRegleEnum.Metier)),
    ...rgEstMaxLengthFactory(250),
    ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),

    'check-nir': {
      pre: [preNotNullOrEmpty, preNotNirTemporaire],
      validateur: (value) => checkNir(value!),
      message: 'Le format du NIR est incorrect',
      level: LevelRegleEnum.Metier,
    },
    'check-nir-api': {
      pre: [preNotNullOrEmpty, preNirTemporaireNon, (_, parent) => (parent.nomNaissance?.length ?? 0) > 2],
      validateur: async (value, parent, _modele, context) => {
        // Voir avec Pierrick pour les pour les personnes en cours d'immatriculation en France
        if (!['1', '2'].includes(value![0])) return

        const result = await context.services.validerNir(value!, parent.nomNaissance!)
        return result.isValidFormat && result.nirDetails !== null
      },
      message: 'Le NIR ne correspond pas au nom de naissance',
      level: LevelRegleEnum.Metier,
    },
    /*
    'check-nir-temporaire': {
      pre: [preNotNullOrEmpty, preNirTemporaire],
      validateur: (value) => checkNirTemporaire(value!),
      message: 'Le format du NIR temporaire est incorrect',
      level: LevelRegleEnum.Metier,
    },
    */
  },
  sexe: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.apprenti?.sexe?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleSexeNir: {
      pre: [preNotNullOrEmpty, targetPre('apprenti.nir', preNotNullOrEmpty), preNotNirTemporaire],
      validateur: (value, parent) => checkNirSexe(parent!.nir!, value!),
      message: "Le sexe de l'apprenti ne correspond pas au NIR saisi",
      level: LevelRegleEnum.Metier,
    },
  },
  dateNaissance: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) => context.status.apprenti?.dateNaissance?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleDateNaissanceNir: {
      pre: [preNotNullOrEmpty, targetPre('apprenti.nir', preNotNullOrEmpty), preNotNirTemporaire],
      validateur: (value, parent) => checkNirDateNaissance(parent!.nir!, value!),
      message: "La date de naissance de l'apprenti ne correspond pas au NIR saisi",
      level: LevelRegleEnum.Metier,
    },
  },
  departementNaissance: {
    ...rgEstMaxLengthFactory(3),
    ...rgTestRegExpFactory(/^((\d{2})|(971|972|973|974|975|976|978|986|987|988)|(2[abAB]))$/),
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.apprenti?.departementNaissance?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleDepartementNir: {
      pre: [preNotNullOrEmpty, targetPre('apprenti.nir', preNotNullOrEmpty), preNotNirTemporaire],
      validateur: (value, parent) => checkNirDepartement(parent!.nir!, value!),
      message: "Le département de naissance de l'apprenti ne correspond pas au NIR saisi",
      level: LevelRegleEnum.Metier,
    },
  },
  communeNaissance: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.apprenti?.communeNaissance?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...rgEstMaxLengthFactory(50),
    ...rgEstCommuneFactory(LevelRegleEnum.Metier),
  },
  nationalite: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.apprenti?.nationalite?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  regimeSocial: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.apprenti?.regimeSocial?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  csp: {
    ...addPre((_value, _parent, _model, context) => context.status.apprenti?.csp?.$status === PropertyStatus.Required, {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    }),
  },
  /*
  statutApprenti: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.apprenti?.statutApprenti?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  */
  situationAvantContrat: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.apprenti?.situationAvantContrat?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  intituleDernierDiplomeOuTitrePrepare: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.apprenti?.intituleDernierDiplomeOuTitrePrepare?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...rgEstMaxLengthFactory(250),
  },
  dernierDiplomeOuTitrePrepare: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.apprenti?.dernierDiplomeOuTitrePrepare?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  derniereAnneeApprenti: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.apprenti?.derniereAnneeApprenti?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controle15ansDateConclusionDerogationDerniereAnnee: {
      pre: [
        preNotNullOrEmpty,
        targetPre('contrat.dateConclusion', preDateValide),
        targetPre('apprenti.dateNaissance', preDateValide),
        preMoins16Ans,
      ],
      validateur: (value, _parent) => !(value === 'InterruptionTroisieme' || value === 'InterruptionQuatrieme'),
      message: "Les apprentis de moins de 16 ans doivent avoir achevé l'année de 3ème",
      level: LevelRegleEnum.Metier,
    },
  },
  diplomeOuTitreLePlusEleveObtenu: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.apprenti?.diplomeOuTitreLePlusEleveObtenu?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  projetCreationOuReprise: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.apprenti?.projetCreationOuReprise?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  inscritSurListeSportifs: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.apprenti?.inscritSurListeSportifs?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  beneficieReconnaissanceTravailleurHandicape: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.apprenti?.beneficieReconnaissanceTravailleurHandicape?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleTypeContratProlongationSuiteReconnaissanceHandicap: {
      pre: [targetPre('contrat.typeContratAvenant', preEquals('ProlongationSuiteReconnaissanceHandicap'))],
      validateur: (value, _parent, _modele) => value === true,
      message:
        "Pour une prolongation du contrat suite à la reconnaissance de l'apprenti comme travailleur handicapé, la case doit être cochée.",
      level: LevelRegleEnum.Metier,
    },
  },
  equivalenceJeune: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.apprenti?.equivalenceJeune?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  extensionBOE: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.apprenti?.extensionBOE?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  adresse: {
    voie: {
      ...rgEstMaxLengthFactory(38),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.apprenti?.adresse?.voie?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstVoieFactory(LevelRegleEnum.Metier),
    },
    complement: {
      ...rgEstMaxLengthFactory(38),
    },
    codePostal: {
      ...rgEstMaxLengthFactory(10),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.apprenti?.adresse?.codePostal?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstCodePostalFactory(LevelRegleEnum.Metier),
    },
    commune: {
      ...rgEstMaxLengthFactory(50),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.apprenti?.adresse?.commune?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstCommuneFactory(LevelRegleEnum.Metier),
    },
  },
  email: {
    ...rgEstMaxLengthFactory(64),
    ...rgEstMailFactory(LevelRegleEnum.Metier),
    ...addPre(
      (_value, _parent, _model, context) => context.status.apprenti?.email?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  telephone: {
    ...rgEstMaxLengthFactory(10),
    ...rgEstNumTelFactory(LevelRegleEnum.Metier),
    ...addPre(
      (_value, _parent, _model, context) => context.status.apprenti?.telephone?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
}

/* FICHIERS */
const reglesFichiers: ReglesCAPPFichier = {}

/* FORMATION ---------------------------------------------------------------------- */
const reglesFormation: ReglesCAPPFormation = {
  referenceFormationInterneEntreprise: {
    ...rgEstMaxLengthFactory(30),
  },
  diplome: {
    ...addPre(
      (_value, _parent, _model, context) => context.status.formation?.diplome?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  typeQualification: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.typeQualification?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  rncp: {
    code: {
      ...addPre(
        (_value, _parent, _model, context) => context.status.formation?.rncp?.code?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      'check-rncp-api': {
        pre: [preNotNullOrEmpty],
        validateur: async (value, _parent, model, context) => {
          const rncp = await context.services.getRncp(value!)

          let message = `Le code RNCP sélectionné ne semble plus actif sur le référentiel FRANCE COMPÉTENCES, nous vous invitons à revoir la certification (RNCP) de cette formation.`

          if (rncp?.certificationDeRemplacement && rncp?.certificationDeRemplacement?.length > 0) {
            message = `Le code RNCP sélectionné ne semble plus actif sur le référentiel FRANCE COMPÉTENCES. Ce code ${rncp!.code} a été remplacé par le ${rncp!.certificationDeRemplacement!.at(0)!.code} . Nous vous invitons à le modifier pour poursuivre la saisie.`
          }

          const reference = isDate(model.formation?.dateDebutCycleFormation)
            ? { date: model.formation?.dateDebutCycleFormation, source: 'début de cycle de formation' }
            : [
                { date: model.contrat?.dateConclusion, source: 'conclusion du contrat' },
                { date: model?.formation?.dateDebutFormationCfa, source: 'début de formation en CFA' },
              ].reduce((refMin, refCur) => {
                  if (isDate(refMin?.date) && isDate(refCur?.date))
                    return refCur.date < refMin.date ? refCur : refMin
                  return refCur
                })

          if (rncp?.dateDecision && isDate(reference?.date) && rncp.dateDecision > reference.date!) {
            message = `Le code RNCP sélectionné ne semble pas actif à la date de ${reference.source}, sur le référentiel FRANCE COMPÉTENCES. Nous vous invitons à le modifier. A défaut, nous ne pourrons engager le contrat et une relance vous sera transmise.`
            return message
          }

          if (rncp?.dateFinEnregistrement) {
            if (
              isDate(reference?.date) &&
              rncp.dateFinEnregistrement < reference.date &&
              !typeContratAvenantRncp.includes(model.contrat?.typeContratAvenant ?? '')
            ) {
              return message
            } else return true
          }

          if (rncp?.estActif === false) {
            return message
          }

          return true
        },
        level: LevelRegleEnum.Transmettre,
      },
    },
  },
  codeDiplome: {
    code: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.formation?.codeDiplome?.code?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
  },
  modalitePedagogique: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.modalitePedagogique?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  estCfaEntreprise: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.estCfaEntreprise?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  dateDebutFormationCfa: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.dateDebutFormationCfa?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    /*
    controleDateApresDateDebutContrat: {
      pre: [preNotNullOrEmpty, preDateValide, targetPre('contrat.dateDebutExecutionContrat', preDateValide)],
      validateur: (value, _parent, modele) => {
        const dateRange: DateRange = getRangeDateDebutFormation(new Date(modele?.contrat?.dateDebutExecutionContrat!))
        const dateValue = new Date(value!)
        return dateRange.start && dateValue < new Date(dateRange.start) ? false : true
      },
      message: 'La date de début de formation doit être supérieure ou égale à la date début du contrat',
      level: LevelRegleEnum.Metier,
    },
    */
    controleDateApres3Mois: {
      pre: [preNotNullOrEmpty, preDateValide, targetPre('contrat.dateDebutExecutionContrat', preDateValide)],
      validateur: (value, _parent, modele) => {
        const dateRange: DateRange = getRangeDateDebutFormation(new Date(modele.contrat!.dateDebutExecutionContrat!))
        const dateValue = new Date(value!)
        return dateRange.end && dateValue > new Date(dateRange.end) ? false : true
      },
      message: 'La date de début de formation intervient + 3 mois après le début du contrat',
      level: LevelRegleEnum.Metier,
    },
  },
  dateDebutCycleFormation: {
    ...rgEstDate,
    'inf date debut contrat': {
      pre: [preNotNullOrEmpty, preDateValide, targetPre('contrat.dateDebutExecutionContrat', preDateValide)],
      validateur: (value, _parent, modele) => {
        return value! <= modele.contrat!.dateDebutExecutionContrat!
      },
      message: 'Doit être inférieure à la date de début du contrat',
      level: LevelRegleEnum.Transmettre,
    },
    'inf date debut formation': {
      pre: [preNotNullOrEmpty, preDateValide, targetPre('formation.dateDebutFormationCfa', preDateValide)],
      validateur: (value, _parent, modele) => {
        return value! <= modele.formation!.dateDebutFormationCfa!
      },
      message: 'Doit être inférieure à la date de début de formation en CFA',
      level: LevelRegleEnum.Transmettre,
    },
  },
  datePrevueFinEpreuvesOuExamens: {
    ...rgEstDate,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.datePrevueFinEpreuvesOuExamens?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleRange: {
      pre: [preNotNullOrEmpty, preDateValide, targetPre('formation.dateDebutFormationCfa', preDateValide)],
      validateur: (value, _parent, modele) =>
        checkDateInRange(
          new Date(value!),
          getRangeDatePrevueFinEpreuvesOuExamens(new Date(modele.formation!.dateDebutFormationCfa!)),
        ),
      message:
        'La date de fin des épreuves ne peut pas être inférieure ou égale à la date de début du cycle de formation',
      level: LevelRegleEnum.Metier,
    },
    controleCDI: {
      pre: [preNotNullOrEmpty, preDateValide, targetPre('contrat.dateFinContratOuPeriodeApprentissage', preDateValide)],
      validateur: (value, _parent, modele) => {
        if (modele.contrat?.modeContractuel === 'DansCadreCdi')
          return (
            new Date(value!).getTime() === new Date(modele.contrat!.dateFinContratOuPeriodeApprentissage!).getTime()
          )
      },
      message:
        'La date prévue de fin des épreuves ou examens doit être identique à la date de fin du contrat pour un CDI.  Veuillez mettre en cohérence les 2 dates.',
      level: LevelRegleEnum.Technique,
    },
  },
  dureeFormationEnHeures: {
    ...rgEstNumeric,
    ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique),
    ...rgEstNumericMaxFactory(5000, LevelRegleEnum.Technique),
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.dureeFormationEnHeures?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    controleQuotite: {
      pre: [
        preNotNullOrEmpty,
        targetPre('contrat.dateDebutExecutionContrat', preDateValide),
        targetPre('contrat.dateFinContratOuPeriodeApprentissage', preDateValide),
        targetPre('contrat.dureeHebdomadaire.heure', preNotNullOrEmpty),
      ],
      validateur: (value, _parent, model) => {
        const nbJour =
          getNbJour(
            new Date(model.contrat!.dateDebutExecutionContrat!),
            new Date(model.contrat!.dateFinContratOuPeriodeApprentissage!),
          ) + 1
        const duree: number =
          Number(model.contrat?.dureeHebdomadaire?.heure) + (model.contrat?.dureeHebdomadaire?.minute ?? 0.0) / 100
        const quotite = (value! / ((((1607 * nbJour) / 365) * duree) / 35)) * 100
        return quotite >= 20
      },
      message:
        'La quotité de formation théorique du contrat d’apprentissage ne peut pas être inférieure à 20% de la durée globale du contrat.',
      level: LevelRegleEnum.Metier,
    },
  },
  dontDureeFormationDistance: {
    ...rgEstNumeric,
    ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique),
    ...rgEstNumericMaxFactory(5000, LevelRegleEnum.Technique),
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.dontDureeFormationDistance?.$status !== PropertyStatus.NotPresent,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        'inférieur-durée-formation': {
          pre: (_value, _model, globalModel, context) =>
            context.status.formation?.dureeFormationEnHeures?.$status !== PropertyStatus.NotPresent &&
            isNumber(globalModel?.formation?.dureeFormationEnHeures),
          level: LevelRegleEnum.Metier,
          message: 'La valeur doit être inférieure à la durée de formation',
          validateur: (_value, model: FormationDTO) => {
            return model.dontDureeFormationDistance! < model.dureeFormationEnHeures!
          },
        },
      },
    ),
  },
  fraisSupportesCfa: {
    montant: {
      ...rgEstNumeric,
      ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique, 'Le montant doit être supérieur à 0'),
      ...rgEstNumericMaxFactory(100000, LevelRegleEnum.Technique, 'Le montant doit être inférieur à 100000'),
      'check-repas': {
        pre: [preNombreValide],
        validateur: (value, parent) => {
          if (parent.nature === 'Repas' && value! > 3) return false
        },
        message: 'Le montant ne peut être supérieur à 3 euros',
        level: LevelRegleEnum.Technique,
      },
      'check-hebergement': {
        pre: [preNombreValide],
        validateur: (value, parent) => {
          if (parent.nature === 'Hebergement' && value! > 6) return false
        },
        message: 'Le montant ne peut être supérieur à 6 euros',
        level: LevelRegleEnum.Technique,
      },
    },
    quantite: {
      ...rgEstNumeric,
      ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique, 'La quantité doit être supérieure à 0'),
      ...rgEstNumericMaxFactory(1000, LevelRegleEnum.Technique, 'La quantité doit être inférieure à 1000'),
    },
  },
  fraisFactureCfa: {
    montant: {
      ...rgEstNumeric,
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.formation?.fraisFactureCfa?.montant?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique, 'Le montant doit être supérieur à 0'),
      ...rgEstNumericMaxFactory(100000, LevelRegleEnum.Technique, 'Le montant doit être inférieur à 100000'),
    },
  },
  lieuFormationIdentiqueCfaPrincipal: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.formation?.lieuFormationIdentiqueCfaPrincipal?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  $model: {
    /* Controle cfaEtablissement et cfaEtablissementLieuPrestation */
    required: {
      pre: (_model, _globalModel, context) => {
        return context.status.formation?.cfaEtablissement?.$status === PropertyStatus.Required
      },
      validateur: (model) => {
        return { cfaEtablissement: isNotNullOrUndefined(model.cfaEtablissement?.siret.valeur) }
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
    'est cfa éligible mandat de gestion': {
      pre: (model, globalModel, context) => {
        if (!model.cfaEtablissement?.siret?.valeur) return false
        if (context?.typeContext !== TypesContextesAutorisesEnum.Prestataire) return false
        return globalModel.estSousMandatGestion === true
      },
      validateur: (value) => {
        if (value.cfaEtablissement!.eligibleMandatGestion === true) return
        return {
          cfaEtablissement: 'Votre CFA ne répond pas aux critères permettant d’avoir recours au mandat de gestion',
        }
      },
      message: 'Le CFA doit être éligible au mandat de gestion',
      level: LevelRegleEnum.Metier,
    },
    controleEtablissement: {
      pre: (_model, globalModel) => isNotNullOrUndefined(globalModel.formation?.cfaEtablissement?.siret?.valeur),
      validateur: (modele) => ({ cfaEtablissement: modele?.cfaEtablissement?.etatAdministratif !== 'F' }),
      message: 'Vous ne pouvez pas sélectionner un établissement inactif, merci de sélectionner un autre établissement',
      level: LevelRegleEnum.Transmettre,
    },
    'required-lieu': {
      pre: (_model, _globalModel, context) =>
        context.status.formation?.cfaEtablissementLieuPrestation?.$status === PropertyStatus.Required,
      validateur: (model) => {
        if (isNullOrUndefined(model.cfaEtablissementLieuPrestation?.siret?.valeur)) {
          return { cfaEtablissementLieuPrestation: false }
        }
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
    'controleEtablissement-lieu': {
      pre: (model) => isNotNullOrUndefined(model?.cfaEtablissementLieuPrestation?.siret?.valeur),
      validateur: (modele) => ({
        cfaEtablissementLieuPrestation: modele?.cfaEtablissementLieuPrestation?.etatAdministratif !== 'F',
      }),
      message: 'Vous ne pouvez pas sélectionner un établissement inactif, merci de sélectionner un autre établissement',
      level: LevelRegleEnum.Transmettre,
    },
    controleCfaLieuPrestationIdentique: {
      pre: (model) =>
        isNotNullOrUndefined(model.cfaEtablissement?.siret?.valeur) &&
        isNotNullOrUndefined(model.cfaEtablissementLieuPrestation?.siret?.valeur),
      validateur: (model) => {
        const estValide =
          model?.cfaEtablissement?.siret?.valeur !== model?.cfaEtablissementLieuPrestation?.siret?.valeur
        return {
          cfaEtablissement: estValide,
          cfaEtablissementLieuPrestation: estValide,
        }
      },
      message: 'Veuillez sélectionner un lieu de formation différent de celui CFA principal',
      level: LevelRegleEnum.Transmettre,
    },
  },
}

/* REMUNERATION ------------------------------------------------------------------- */
const reglesRemuneration: ReglesCAPPRemuneration = {
  salaireBrutMensuelEmbauche: {
    ...rgEstNumeric,
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.remuneration?.salaireBrutMensuelEmbauche?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
    ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique),
    ...rgEstNumericMaxFactory(10000, LevelRegleEnum.Technique),
    'min-smic': {
      pre: [
        preNotNullOrEmpty,
        targetPre('contrat.dureeHebdomadaire.heure', preNotNullOrEmpty),
        targetPre('contrat.dateDebutExecutionContrat', preDateValide),
      ],
      validateur: (value, _parent, model) => {
        if (!isNumber(value)) return
        let minPourcentage =
          model.remuneration
            ?.ventilations!.filter((x) => x.baseCalcul === 'SMIC')
            .map((v) => v.pourcentage)
            .filter((p): p is number => p !== null)
            .reduce((min, p) => Math.min(min, p), Number.POSITIVE_INFINITY) ?? 0

        if (minPourcentage === 0 || minPourcentage === Infinity) {
          const remuSmicMinium = getRemunerationProperty(
            new Date(model.apprenti!.dateNaissance!),
            new Date(model.contrat!.dateDebutExecutionContrat!),
            new Date(model.contrat!.dateFinContratOuPeriodeApprentissage!),
          )

          minPourcentage =
            remuSmicMinium
              ?.map((v) => v.minPourcentage)
              .filter((p): p is number => p !== null)
              .reduce((min, p) => Math.min(min, p), Number.POSITIVE_INFINITY) ?? 0
        }

        if (minPourcentage === 0 || minPourcentage === Infinity) return

        const semainesParMois = 52 / 12
        const heureHebdo: number =
          Number(model.contrat?.dureeHebdomadaire?.heure) + (model.contrat?.dureeHebdomadaire?.minute ?? 0.0) / 100

        const heureHebdoBase: number = 35

        let smicMin = 0
        const smicHorairebrute = model.employeur?.etablissement?.adresse?.codePostal?.startsWith(CODE_POSTAL_MAYOTTE)
          ? getSmicHoraireBrut('MAYOTTE', new Date(model.contrat!.dateDebutExecutionContrat!))
          : getSmicHoraireBrut('METROPOLE', new Date(model.contrat!.dateDebutExecutionContrat!))

        // moins de 35h
        if (heureHebdo <= heureHebdoBase) {
          smicMin = Math.round(smicHorairebrute * heureHebdo * semainesParMois * minPourcentage * 100) / 100
        } else {
          // plus de 35h

          // 25%
          let pourcentageHeureSupp = 1.25

          // si IDCC HCR => 10%
          if (model.employeur?.etablissement?.idcc?.code === '1979') pourcentageHeureSupp = 1.1

          const heureHebdoSupp = heureHebdo - heureHebdoBase
          smicMin =
            Math.round(
              (smicHorairebrute * heureHebdoBase * semainesParMois +
                smicHorairebrute * heureHebdoSupp * semainesParMois * pourcentageHeureSupp) *
                minPourcentage *
                100,
            ) / 100
        }

        if (smicMin <= value) return

        return (
          'Le salaire brut à l’embauche doit être supérieur à ' +
          (minPourcentage * 100).toFixed(0) +
          '% du SMIC, soit ' +
          smicMin +
          '€'
        )
      },

      level: LevelRegleEnum.Transmettre,
    },
  },

  avantageLogement: {
    ...rgEstNumeric,
    ...rgEstNumericMinFactory(-1, LevelRegleEnum.Technique, 'La valeur doit être supérieure ou égale à 0'),
    ...rgEstNumericMaxFactory(10000, LevelRegleEnum.Technique),
  },
  avantageNourriture: {
    ...rgEstNumeric,
    ...rgEstNumericMinFactory(-1, LevelRegleEnum.Technique, 'La valeur doit être supérieure ou égale à 0'),
    ...rgEstNumericMaxFactory(100, LevelRegleEnum.Technique),
  },
  ventilations: {
    pourcentage: {
      ...rgEstNumeric,
      ...rgEstNumericMinFactory(0, LevelRegleEnum.Technique),
      ...rgEstNumericMaxFactory(10, LevelRegleEnum.Technique, 'La valeur doit être inférieure à 1000'),
    },
    dateRange: {
      start: {
        ...rgEstDate,
      },
      end: {
        ...rgEstDate,
      },
    },
  },
}

/* REPRESENTANT LEGAL ------------------------------------------------------------- */
const reglesRepresentantLegal: ReglesCAPPRepresentantLegal = {
  mineurEmancipe: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.representantLegal?.mineurEmancipe?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  estStructure: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.representantLegal?.estStructure?.$status === PropertyStatus.Required,
      {
        ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  representant: {
    nom: {
      ...rgEstMaxLengthFactory(50),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.representantLegal?.representant?.nom?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    prenom: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.representantLegal?.representant?.prenom?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    email: {
      ...rgEstMaxLengthFactory(200),
      ...rgEstMailFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.representantLegal?.representant?.email?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    telephone: {
      ...rgEstMaxLengthFactory(10),
      ...rgEstNumTelFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.representantLegal?.representant?.telephone?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
  },
  adresse: {
    voie: {
      ...rgEstMaxLengthFactory(38),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.representantLegal?.adresse?.voie?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstVoieFactory(LevelRegleEnum.Metier),
    },
    complement: {
      ...rgEstMaxLengthFactory(38),
      // ...rgEstVoieFactory(LevelRegleEnum.Metier),
    },
    codePostal: {
      ...rgEstMaxLengthFactory(10),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.representantLegal?.adresse?.codePostal?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstCodePostalFactory(LevelRegleEnum.Metier),
    },
    commune: {
      ...rgEstMaxLengthFactory(50),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.representantLegal?.adresse?.commune?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstCommuneFactory(LevelRegleEnum.Metier),
    },
  },
  /*
  estStructure: {
    controleStructure: {
      pre: [
        targetPre('contrat.dateConclusion', preDateValide),
        targetPre('apprenti.dateNaissance', preDateValide),
        preMineur,
        preMineurEmancipe,
      ],
      validateur: (value) => value === true,
      message: 'Cocher la case si le représentant légal est une structure',
      level: LevelRegleEnum.Metier,
    },
  },
  */
}

/* TUTORAT ------------------------------------------------------------- */
const reglesTutorat: ReglesCAPPTutorat = {
  maitreApprentissage: {
    nomNaissance: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.maitreApprentissage?.nomNaissance?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    nom: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.maitreApprentissage?.nom?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    prenom: {
      ...rgEstMaxLengthFactory(50),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.maitreApprentissage?.prenom?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    dateNaissance: {
      ...rgEstDate,
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.maitreApprentissage?.dateNaissance?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      controle18Ans: {
        pre: [preNotNullOrEmpty],
        validateur: (value, _parent, _modele) => {
          const age = getAge(new Date(value!), getToday())
          return age >= 18
        },
        message: "Le maître d'apprentissage doit être majeur",
        level: LevelRegleEnum.Metier,
      },
      controleMaximum: {
        pre: [preNotNullOrEmpty],
        validateur: (value, _parent, _modele) => {
          const age = getAge(new Date(value!), getToday())
          return age <= 100
        },
        message: "L'âge du maître d'apprentissage semble incorrect",
        level: LevelRegleEnum.Metier,
      },
    },
    email: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.maitreApprentissage?.email?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstMaxLengthFactory(64),
      ...rgEstMailFactory(LevelRegleEnum.Metier),
    },
    emploiOccupe: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.maitreApprentissage?.emploiOccupe?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstMaxLengthFactory(100),
    },
    /*
    diplomeOuTitreLePlusEleveObtenu: {
      ...rgEstMaxLengthFactory(255),
    },
    */
    sexe: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.maitreApprentissage?.sexe?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
  },
  maitreApprentissageSecondaire: {
    nomNaissance: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.maitreApprentissageSecondaire?.nomNaissance?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    nom: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.maitreApprentissageSecondaire?.nom?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    prenom: {
      ...rgEstMaxLengthFactory(50),
      ...rgEstNomOuPrenomFactory(LevelRegleEnum.Metier),
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.maitreApprentissageSecondaire?.prenom?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    dateNaissance: {
      ...rgEstDate,
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.maitreApprentissageSecondaire?.dateNaissance?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      controle18Ans: {
        pre: [preNotNullOrEmpty],
        validateur: (value, _parent, _modele) => {
          const age = getAge(new Date(value!), getToday())
          return age >= 18
        },
        message: "Le maître d'apprentissage doit être majeur",
        level: LevelRegleEnum.Metier,
      },
      controleMaximum: {
        pre: [preNotNullOrEmpty],
        validateur: (value, _parent, _modele) => {
          const age = getAge(new Date(value!), getToday())
          return age <= 100
        },
        message: "L'âge du maître d'apprentissage semble incorrect",
        level: LevelRegleEnum.Metier,
      },
    },
    email: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.maitreApprentissageSecondaire?.email?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstMaxLengthFactory(64),
      ...rgEstMailFactory(LevelRegleEnum.Metier),
    },
    emploiOccupe: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.maitreApprentissageSecondaire?.emploiOccupe?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
      ...rgEstMaxLengthFactory(100),
    },
    sexe: {
      ...addPre(
        (_value, _parent, _model, context) =>
          context.status.tutorat?.maitreApprentissageSecondaire?.sexe?.$status === PropertyStatus.Required,
        {
          ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
        },
      ),
    },
    /*
    diplomeOuTitreLePlusEleveObtenu: {
      ...rgEstMaxLengthFactory(255),
    },*/
  },
  attestationTuteurCriteresEligibilite: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.tutorat?.attestationTuteurCriteresEligibilite?.$status === PropertyStatus.Required,
      {
        ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  attestationTuteurFormationTuteur: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.tutorat?.attestationTuteurFormationTuteur?.$status === PropertyStatus.Required,
      {
        ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  attestationTuteurTroisApprentis: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.tutorat?.attestationTuteurTroisApprentis?.$status === PropertyStatus.Required,
      {
        ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
}

/* REGLES  ------------------------------------------------------------------------ */
export const reglesConfiguration: ReglesCAPP = {
  validationNotice: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  attestationSurHonneur: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  attestationPiecesJustificatives: {
    ...addPre(
      (_value, _parent, _model, context) =>
        context.status.attestationPiecesJustificatives?.$status === PropertyStatus.Required,
      {
        ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
      },
    ),
  },
  acceptationCg: {
    ...rgEstVraiFactory(LevelRegleEnum.Transmettre),
  },
  employeur: reglesEmployeur,
  contrat: reglesContrat,
  apprenti: reglesApprenti,
  fichiers: reglesFichiers,
  formation: reglesFormation,
  remuneration: reglesRemuneration,
  representantLegal: reglesRepresentantLegal,
  tutorat: reglesTutorat,
  lieuSignature: {
    ...addPre((_value, _parent, _model, context) => context.status.lieuSignature?.$status === PropertyStatus.Required, {
      ...rgEstObligatoireFactory(LevelRegleEnum.Transmettre),
    }),
  },

  $model: {
    controle15ans: {
      pre: (model, _globalModel, context) => {
        return (
          preDateValide(model.apprenti?.dateNaissance) &&
          preAdherentOuMandatGestion(context.typeContext, model.estSousMandatGestion)
        )
      },
      validateur: (model) => {
        const result: ModelValidateurResult<ContratApprentissageDTO> = {}

        const dateNaissance = new Date(model.apprenti!.dateNaissance!)
        let dateConclusion = getToday()
        if (isDate(model.contrat?.dateConclusion)) {
          dateConclusion = new Date(model.contrat.dateConclusion!)
        }

        let dateNaissanceAnnee = new Date(dateNaissance)
        dateNaissanceAnnee.setFullYear(dateConclusion.getFullYear())

        let is15ans1JourDateExecutionContrat = true

        if (isDate(model.contrat?.dateDebutExecutionContrat)) {
          const dateDebutExecutionContrat = new Date(model.contrat.dateDebutExecutionContrat)

          dateNaissanceAnnee = new Date(model.apprenti!.dateNaissance!)
          dateNaissanceAnnee.setFullYear(dateDebutExecutionContrat.getFullYear())

          const ageDateExecution = getAge(dateNaissance, dateDebutExecutionContrat)
          const nbJourDateExecution = getNbJour(dateNaissanceAnnee, dateDebutExecutionContrat)

          // Contrôle âge 15 ans et 1 jour à la date d'exécution du contrat
          is15ans1JourDateExecutionContrat =
            ageDateExecution > 15 || (ageDateExecution === 15 && nbJourDateExecution >= 1)
        }

        /* PRT-784
        const dateConclusionFinAnnee = getFinAnneeDateReference(dateConclusion)
        const ageDateConclusionFinAnnee = getAge(dateNaissance, dateConclusionFinAnnee)
        const nbJourDateConclusion = getNbJour(dateNaissanceAnnee, dateConclusionFinAnnee)

        // Contrôle âge 15 et 1 jour à la date de fin d'année de la date de conclusion ou date du jour
        const is15ans1JourDateConclusionFinAnnee =
          ageDateConclusionFinAnnee > 15 || (ageDateConclusionFinAnnee === 15 && nbJourDateConclusion >= 1)

        if (is15ans1JourDateConclusionFinAnnee === false || is15ans1JourDateExecutionContrat === false) {
        */
        if (!is15ans1JourDateExecutionContrat) {
          //result['contrat.dateConclusion'] =
          result['apprenti.dateNaissance'] = result['contrat.dateDebutExecutionContrat'] =
            "L'apprenti doit être âgé de 15 ans et un jour au 31/12 de l'année civile et à la date d'exécution du contrat"
          return result
        }

        // Contrôle dérogation pour 15 ans et 1 jour et moins de 16 ans
        const ageDateConclusionFin = getAge(dateNaissance, dateConclusion)
        if (
          ageDateConclusionFin < 16 &&
          model.contrat?.typeDerogation !== 'AgeInferieur16' &&
          model.contrat?.typeDerogation !== 'CumulDerogations'
        ) {
          result['contrat.dateConclusion'] =
            result['apprenti.dateNaissance'] =
            result['contrat.typeDerogation'] =
              "Sauf dérogation et conditions, l'apprenti doit être ägé de 16 ans à la date de conclusion du contrat"
          return result
        }
      },
      level: LevelRegleEnum.Metier,
    },
    controlePlus30ansDateConclusionDerogation: {
      pre: (model, _globalModel, context) => {
        return (
          preDateValide(model.contrat?.dateConclusion) &&
          preDateValide(model.apprenti?.dateNaissance) &&
          preAdherentOuMandatGestion(context.typeContext, model.estSousMandatGestion)
        )
      },
      validateur: (model) => {
        const result: ModelValidateurResult<ContratApprentissageDTO> = {}
        const age = getAge(new Date(model.apprenti!.dateNaissance!), new Date(model.contrat!.dateConclusion!))
        const derogations: string[] = ['AgeSuperieur29', 'CumulDerogations', 'AutreDerogation']
        let messageErreur: string | null = null
        const isApprentiParticularite =
          model.apprenti?.beneficieReconnaissanceTravailleurHandicape === true ||
          model.apprenti?.inscritSurListeSportifs === true ||
          model.apprenti?.projetCreationOuReprise === true

        const isSuccessionContrat = typeContratSuccessionContrat.includes(model.contrat?.typeContratAvenant ?? '')
        const isDerogation = derogations.includes(model.contrat?.typeDerogation ?? '')

        if (age >= 30) {
          if (!isDerogation && isApprentiParticularite === true) {
            messageErreur =
              'Sauf situation particulière (travailleur handicapé, sportif de haut niveau, création ou reprise d’entreprise) l’apprenti doit avoir moins de 30 ans à la date de signature du contrat.'
          } else {
            if (isApprentiParticularite === false && (!isDerogation || !isSuccessionContrat || age > 35))
              messageErreur =
                'Sauf situation particulière (travailleur handicapé, sportif de haut niveau, création ou reprise d’entreprise) ou nouveau contrat, l’apprenti doit avoir moins de 30 ans à la date de signature du contrat.'
          }
        }

        if (messageErreur !== null) {
          result['contrat.dateConclusion'] =
            result['contrat.typeDerogation'] =
            result['apprenti.dateNaissance'] =
              messageErreur
          if (!isApprentiParticularite) result['contrat.typeContratAvenant'] = messageErreur
        }

        return result
      },
      level: LevelRegleEnum.Metier,
    },

    controleDateFinContratEtFormation: {
      pre: (model) => {
        return (
          preDateValide(model.contrat?.dateFinContratOuPeriodeApprentissage) &&
          preDateValide(model.formation?.datePrevueFinEpreuvesOuExamens)
        )
      },
      validateur: (model) => {
        const dateFinContratOuPeriodeApprentissage: Date = new Date(
          model.contrat!.dateFinContratOuPeriodeApprentissage!,
        )
        const datePrevueFinEpreuvesOuExamens: Date = new Date(model.formation!.datePrevueFinEpreuvesOuExamens!)

        if (
          dateFinContratOuPeriodeApprentissage < datePrevueFinEpreuvesOuExamens &&
          model.contrat?.modeContractuel !== 'DansCadreCdi'
        )
          return {
            'contrat.dateFinContratOuPeriodeApprentissage': false,
            'formation.datePrevueFinEpreuvesOuExamens': false,
          }
      },
      message: 'La date prévue de fin des épreuves doit intervenir avant la fin du contrat.',
      level: LevelRegleEnum.Metier,
    },
    /*
    controleDateFinContratPlus2MoisFinFormation: {
      pre: (model) => {
        return (
          preNotNullOrEmpty(model.contrat?.dateFinContratOuPeriodeApprentissage) &&
          preNotNullOrEmpty(model.formation?.datePrevueFinEpreuvesOuExamens) &&
          preDateValide(model.contrat?.dateFinContratOuPeriodeApprentissage) &&
          preDateValide(model.formation?.datePrevueFinEpreuvesOuExamens)
        )
      },
      validateur: (model) => {
        const dateFinContratOuPeriodeApprentissage: Date = new Date(
          model.contrat?.dateFinContratOuPeriodeApprentissage!,
        )
        const datePrevueFinEpreuvesOuExamens: Date = new Date(model.formation?.datePrevueFinEpreuvesOuExamens!)
        const dateFinContratOuPeriodeApprentissageMax: Date = addMonth(datePrevueFinEpreuvesOuExamens, 2)
        dateFinContratOuPeriodeApprentissageMax.setDate(dateFinContratOuPeriodeApprentissageMax.getDate() + 2)

        if (dateFinContratOuPeriodeApprentissage > dateFinContratOuPeriodeApprentissageMax)
          return {
            'contrat.dateFinContratOuPeriodeApprentissage': false,
            'formation.datePrevueFinEpreuvesOuExamens': false,
          }
      },
      message: 'La Date de fin de contrat intervient + 2 mois et 2 jours après la date de fin de formation',
      level: LevelRegleEnum.Metier,
    },
    */
    controleRemuneration: {
      pre: (model, _globalModel, context) => {
        return (
          preDateValide(model.apprenti?.dateNaissance) &&
          preDateValide(model.contrat?.dateDebutExecutionContrat) &&
          preDateValide(model.contrat?.dateFinContratOuPeriodeApprentissage) &&
          isAdherentOuMandatGestion(context.typeContext, model.estSousMandatGestion)
        )
      },
      validateur: (model) => {
        const result: ModelValidateurResult<ContratApprentissageDTO> = {}
        if (
          model.remuneration?.ventilations &&
          model.remuneration?.ventilations !== null &&
          model.remuneration?.ventilations != undefined
        ) {
          const remunerationProperty = getRemunerationProperty(
            new Date(model.apprenti!.dateNaissance!),
            new Date(model.contrat!.dateDebutExecutionContrat!),
            new Date(model.contrat!.dateFinContratOuPeriodeApprentissage!),
          )

          for (const [index, prop] of remunerationProperty.entries()) {
            if (
              prop.date?.start &&
              prop.minPourcentage &&
              isNotNullOrUndefined(model.remuneration?.ventilations?.at(index)?.pourcentage) &&
              model.remuneration!.ventilations!.at(index)!.pourcentage! < prop.minPourcentage &&
              model.remuneration!.ventilations!.at(index)!.baseCalcul === 'SMIC'
            ) {
              result[`remuneration.ventilations.${index}.pourcentage`] =
                'Le pourcentage minimum est de ' + (prop.minPourcentage * 100).toFixed(0) + '%'
            }

            if (prop.date?.start && model.remuneration?.ventilations?.at(index)?.dateRange?.start !== prop.date.start) {
              result[`remuneration.ventilations.${index}.dateRange.start`] =
                'La date de début de la période est ' + dateFormatter(prop.date?.start)
            }

            if (prop.date?.end && model.remuneration?.ventilations?.at(index)?.dateRange?.end !== prop.date.end) {
              result[`remuneration.ventilations.${index}.dateRange.end`] =
                'La date de fin de la période est ' + dateFormatter(prop.date?.end)
            }
          }
        }
        return result
      },
      level: LevelRegleEnum.Metier,
    },
    controleObligatoireRemuneration: {
      pre: (model, _globalModel, context) => {
        return (
          preDateValide(model.apprenti?.dateNaissance) &&
          preDateValide(model.contrat?.dateDebutExecutionContrat) &&
          preDateValide(model.contrat?.dateFinContratOuPeriodeApprentissage) &&
          isAdherentOuMandatGestion(context.typeContext, model.estSousMandatGestion)
        )
      },
      validateur: (model) => {
        const result: ModelValidateurResult<ContratApprentissageDTO> = {}
        if (isNotNullOrUndefined(model.remuneration?.ventilations)) {
          const remunerationProperty = getRemunerationProperty(
            new Date(model.apprenti!.dateNaissance!),
            new Date(model.contrat!.dateDebutExecutionContrat!),
            new Date(model.contrat!.dateFinContratOuPeriodeApprentissage!),
          )

          for (const [index, prop] of remunerationProperty.entries()) {
            if (
              prop.date?.start &&
              isNullUndefinedOrEmptyString(model.remuneration?.ventilations?.at(index)?.pourcentage)
            ) {
              result[`remuneration.ventilations.${index}.pourcentage`] = SAISIE_OBLIGATOIRE
            }

            if (
              prop.date?.start &&
              isNullUndefinedOrEmptyString(model.remuneration?.ventilations?.at(index)?.baseCalcul)
            ) {
              result[`remuneration.ventilations.${index}.baseCalcul`] = SAISIE_OBLIGATOIRE
            }
          }
        }
        return result
      },
      level: LevelRegleEnum.Transmettre,
    },
    controleObligatoireFraisSupportesCfa: {
      pre: (model) => {
        return !isNullOrUndefined(model.formation?.fraisSupportesCfa)
      },
      validateur: (model, _globalModel, context) => {
        const result: ModelValidateurResult<ContratApprentissageDTO> = {}

        for (const [index, prop] of model.formation!.fraisSupportesCfa!.entries()) {
          if (
            context.status.formation?.fraisSupportesCfa?.at(index)?.montant?.$status === PropertyStatus.Required &&
            isNullUndefinedOrEmptyString(prop.montant)
          ) {
            result[`formation.fraisSupportesCfa.${index}.montant`] = SAISIE_OBLIGATOIRE
          }

          if (
            context.status.formation?.fraisSupportesCfa?.at(index)?.quantite?.$status === PropertyStatus.Required &&
            isNullUndefinedOrEmptyString(prop.quantite)
          ) {
            result[`formation.fraisSupportesCfa.${index}.quantite`] = SAISIE_OBLIGATOIRE
          }
        }
        return result
      },
      level: LevelRegleEnum.Metier,
    },
    obligatoire: {
      validateur: (model) => {
        const result: ModelValidateurResult<ReglesCAPP> = {}
        if (!model.apprenti) result.apprenti = false
        if (!model.contrat) result.contrat = false
        if (!model.fichiers) result.fichiers = false
        if (!model.formation) result.formation = false
        if (!model.remuneration) result.remuneration = false
        if (!model.tutorat) result.tutorat = false
        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Metier,
    },
    /*
    controleCfaObligatoire: {
      validateur: (model) => {
        const result: ModelValidateurResult<ContratApprentissageDTO> = {}
        if (!model.formation?.cfaEtablissement) result['formation.cfaEtablissement'] = false

        if (!model.formation?.lieuFormationIdentiqueCfaPrincipal && !model.formation?.cfaEtablissementLieuPrestation)
          result['formation.cfaEtablissementLieuPrestation'] = false

        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
    */
    controleCodeDiplomeRncp: {
      validateur: (model, _globalModel, context) => {
        const result: ModelValidateurResult<ContratApprentissageDTO> = {}
        if (
          context.status.formation?.codeDiplome?.code?.$status === PropertyStatus.Required &&
          (isNullOrUndefined(model.formation?.codeDiplome) || isNullOrUndefined(model.formation?.codeDiplome?.code))
        )
          result['formation.codeDiplome.code'] = false

        if (
          context.status.formation?.rncp?.code?.$status === PropertyStatus.Required &&
          (isNullOrUndefined(model.formation?.rncp) || isNullOrUndefined(model.formation?.rncp?.code))
        )
          result['formation.rncp.code'] = false

        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },

    controleFichier: {
      validateur: (model, _globalModel, context) => {
        const result: ModelValidateurResult<ContratApprentissageDTO> = {}

        if (isAdherentOuMandatGestion(context.typeContext, model.estSousMandatGestion)) {
          for (const doc of context.naturesFichiers.obligatoires) {
            if (model.fichiers?.filter((x) => x.nature === doc).length === 0) {
              result[`fichiers`] = false
            }
          }
        }
        return result
      },
      message: SAISIE_OBLIGATOIRE,
      level: LevelRegleEnum.Transmettre,
    },
  },
} as const
